import React, { useMemo, useState, useCallback } from 'react';

import moment from 'moment';
import * as Yup from 'yup';

import { Report } from '~/components';
import {
  DateTime,
  Autocomplete,
  MultiAutocomplete,
  RadioGroup,
} from '~/components/Form';
import { PesquisaTabela } from '~/components/Form/v2';
import { t } from '~/helpers';

function RelDrenagemTanque() {
  const [open, setOpen] = useState(true);
  const [postos, setPostos] = useState([]);
  const [usuario, setUsuario] = useState(null);

  const start = moment()
    .subtract({ day: 7 })
    .set({ hour: 0, minute: 0, second: 0 });
  const end = moment().set({ hour: 23, minute: 59, second: 59 });

  const selectedPostoId =
    postos && postos.length === 1 ? postos[0].idposto : '';

  const formatoOptions = useMemo(
    () => [
      { label: 'PDF', value: 'pdf' },
      { label: 'Excel', value: 'excel' },
    ],
    []
  );

  const cellsUser = useMemo(
    () => [
      {
        id: 'codigousuario',
        label: t('s.cadastro.usuario.codigousuario'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeusuario',
        label: t('s.cadastro.usuario.nomeusuario'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const tanqueFisicoParams = useMemo(
    () => ({
      wherefield: ['idposto'],
      valuewherefield: [selectedPostoId],
      typewherefield: ['EQUALS'],
      fieldlist: 'idposto, idtanquefisico, codigotanquefisico',
    }),
    [selectedPostoId]
  );

  const handlePostosChange = useCallback((value) => {
    setPostos(value || []);
  }, []);

  const handleValidate = useCallback(async (params) => {
    try {
      const schema = Yup.object().shape({
        datahorainicial: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        datahorafinal: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
      });

      await schema.validate(params, {
        abortEarly: false,
      });
    } catch (err) {
      const form = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          form[error.path] = error.message.replace(`${error.path} `, '');
        });

        throw { form };
      }
    }
  }, []);

  function handleSerializeForm(params) {
    const idPosto =
      postos && postos.length > 0 ? postos.map((posto) => posto.idposto) : '';

    return {
      ...params,
      idposto: idPosto,
      idtanquefisico: params.idtanquefisico || '',
      idusuario: params.codigousuario ? usuario?.idusuario || '' : '',
    };
  }

  return (
    <Report
      open={open}
      onClose={() => setOpen(false)}
      title={t('s.relatorio.drenagemtanque')}
      relatorio="reldrenagemtanque"
      serialize={(param) => handleSerializeForm(param)}
      validate={(param) => handleValidate(param)}
      initials={{
        datahorainicial: start.format('DD/MM/YYYY HH:mm:ss'),
        datahorafinal: end.format('DD/MM/YYYY HH:mm:ss'),
        formato: 'pdf',
      }}
    >
      <>
        <DateTime
          label={t('s.geral.opcoes.datahorainicial')}
          name="datahorainicial"
          serialize={(value) =>
            value.set({ seconds: 0 }).format('DD/MM/YYYY HH:mm:ss')
          }
          time
        />

        <DateTime
          label={t('s.geral.opcoes.datahorafinal')}
          name="datahorafinal"
          serialize={(value) =>
            value.set({ seconds: 59 }).format('DD/MM/YYYY HH:mm:ss')
          }
          time
        />

        <MultiAutocomplete
          multiple
          name="idposto"
          label="codigonomeposto"
          url="postoscomboios"
          unique="idposto"
          title={t('s.geral.postocomboio')}
          hiddens={[{ name: 'codigonomeposto' }]}
          getValue={handlePostosChange}
        />

        <Autocomplete
          key={`tanquefisico-${selectedPostoId}`}
          disabled={!selectedPostoId}
          name="idtanquefisico"
          label="codigotanquefisico"
          url="tanquesfisicos"
          unique="idtanquefisico"
          title={t('s.configuracao.tanquefisico.idtanquefisico')}
          params={tanqueFisicoParams}
        />

        <PesquisaTabela
          name="codigousuario"
          label={t('s.operacao.recebimento.codigousuario')}
          primary="codigousuario"
          secondary="nomeusuario"
          url="usuarios"
          header={cellsUser}
          getValue={setUsuario}
        />

        <RadioGroup
          name="formato"
          label={t('s.relatorio.geral.formato')}
          options={formatoOptions}
          defaultValue="pdf"
          serialize={(value) => value}
          row
        />
      </>
    </Report>
  );
}

export default RelDrenagemTanque;
